/** Public catalog host (not the template editor at templates.containd.app). */
export const CATALOG_PUBLIC_HOST = 'catalog.containd.app';

/** Public install JSON for a catalog package (Hosting rewrite → Functions). */
export function catalogPublicPackageUrl(slug: string, semver: string): string {
  return `https://${CATALOG_PUBLIC_HOST}/packages/${encodeURIComponent(slug.trim())}/${encodeURIComponent(semver.trim())}.json`;
}

/** Public sample-project JSON (Hosting rewrite → Functions; default Storage bucket). */
export function catalogPublicProjectUrl(slug: string): string {
  return `https://${CATALOG_PUBLIC_HOST}/projects/${encodeURIComponent(slug.trim())}.json`;
}

/** GCS object for a published sample project on the default app bucket. */
export function catalogProjectObjectPath(slug: string): string {
  return `catalog-site/projects/${slug.trim()}.json`;
}

/** Browser download filename for a versioned package JSON. */
export function catalogPackageDownloadFilename(
  slug: string,
  semver: string,
  kind: 'package' | 'categories' = 'package',
): string {
  const s = slug.trim();
  const v = semver.trim();
  return kind === 'categories' ? `${s}-${v}.categories.json` : `${s}-${v}.json`;
}

/** Browser download filename for a sample project JSON. */
export function catalogProjectDownloadFilename(slug: string): string {
  return `${slug.trim()}-project.json`;
}

function decodePathSegment(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    return decodeURIComponent(trimmed);
  } catch {
    return null;
  }
}

/**
 * Download filename for `/packages/{slug}/{file}` (`{semver}.json` or `{semver}.categories.json`).
 */
export function catalogDownloadFilenameForPackageFile(
  slug: string,
  file: string,
): string | null {
  const decodedSlug = decodePathSegment(slug);
  const decodedFile = decodePathSegment(file);
  if (!decodedSlug || !decodedFile) return null;
  if (decodedFile.includes('..') || decodedFile.includes('/') || decodedFile.includes('\\')) {
    return null;
  }
  const categories = /^(.+)\.categories\.json$/i.exec(decodedFile);
  if (categories) return catalogPackageDownloadFilename(decodedSlug, categories[1], 'categories');
  const json = /^(.+)\.json$/i.exec(decodedFile);
  if (json) return catalogPackageDownloadFilename(decodedSlug, json[1]);
  return null;
}

/**
 * Template slug from `/projects/{file}` (`{slug}.json` or `{slug}-sample.json`).
 */
export function catalogProjectSlugFromDownloadFile(file: string): string | null {
  const decoded = decodePathSegment(file);
  if (!decoded) return null;
  const name = decoded.replace(/^\/+/, '');
  if (!name || name.includes('..') || name.includes('/') || name.includes('\\')) return null;
  const lower = name.toLowerCase();
  if (lower.endsWith('-sample.json')) {
    const slug = name.slice(0, -'-sample.json'.length).trim();
    return slug || null;
  }
  if (lower.endsWith('.json')) {
    const slug = name.slice(0, -'.json'.length).trim();
    return slug || null;
  }
  return null;
}

/**
 * Default-bucket GCS objects to try for `/projects/{file}`.
 * Canonical path is `catalog-site/projects/{slug}.json`; `{slug}-sample.json` is also accepted.
 */
export function catalogProjectServeObjectPaths(file: string): string[] | null {
  if (file.includes('/') || file.includes('\\') || file.includes('..')) return null;
  const slug = catalogProjectSlugFromDownloadFile(file);
  if (!slug) return null;
  const requested = `catalog-site/projects/${file.replace(/^\/+/, '')}`;
  return [...new Set([requested, catalogProjectObjectPath(slug)])];
}

function pushHttpsUrl(urls: string[], raw: unknown): void {
  if (typeof raw !== 'string') return;
  const url = raw.trim();
  if (!/^https:\/\//i.test(url)) return;
  if (!urls.includes(url)) urls.push(url);
}

/**
 * HTTPS URLs for a catalog package JSON. Prefer these over Firebase Storage
 * `getBytes` from browser origins (admin SPA CORS on the default bucket).
 */
export function catalogPackageHttpsUrls(data: {
  installUrl?: unknown;
  packageUrl?: unknown;
  slug?: unknown;
  semver?: unknown;
}): string[] {
  const urls: string[] = [];
  pushHttpsUrl(urls, data.installUrl);
  pushHttpsUrl(urls, data.packageUrl);
  const slug = typeof data.slug === 'string' ? data.slug.trim() : '';
  const semver = typeof data.semver === 'string' ? data.semver.trim() : '';
  if (slug && semver) pushHttpsUrl(urls, catalogPublicPackageUrl(slug, semver));
  return urls;
}

/** Public shares / Discover front door (VISION static Spaces at /s/{slug}). */
export const SHARES_PUBLIC_HOST = 'containd.space';

/** App install / get-Containd marketing site. */
export const CATALOG_APP_INSTALL_URL = 'https://www.containd.app';

/** Containd Expo / web app (publish online). */
export const CONTAIND_WEB_APP_URL = 'https://app.containd.app';

/** Official store listings. */
export const CONTAIND_IOS_STORE_URL = 'https://apps.apple.com/app/id6752220681';
export const CONTAIND_ANDROID_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.nxtlvlordereng.containd';

/** Corporate / Squarespace brand site. */
export const CATALOG_CORPORATE_URL = 'https://www.nextlevelorder.com';

/**
 * Indexed by default since the public launch (WP6).
 * Override at build/runtime with env `CATALOG_ROBOTS_NOINDEX=1` to force noindex
 * (emergency), or `CATALOG_ROBOTS_NOINDEX=0` to force indexing.
 */
export const CATALOG_SOFT_LAUNCH_NOINDEX = false;

/** UI locales for the SEO catalog site (Containd uses `jp`, not `ja`). */
export const CATALOG_SITE_LOCALES = ['en', 'de', 'jp'] as const;

export type CatalogSiteLocale = (typeof CATALOG_SITE_LOCALES)[number];

/** Legal / company links — mirrors containd `constants/policyUrls.ts` (web footer). */
export const CATALOG_LEGAL_URLS = {
  eula: 'https://www.nextlevelorderengineering.com/EULA',
  privacy: 'https://www.nextlevelorderengineering.com/privacy',
  tos: 'https://www.nextlevelorderengineering.com/ToS',
  imprint: 'https://www.nextlevelorderengineering.com/impressum',
} as const;

export const CATALOG_COMPANY_LEGAL_NAME =
  'Next Level Order Engineering UG (haftungsbeschränkt)';

/** Template authoring editor (not this catalog). */
export const CATALOG_EDITOR_URL = 'https://templates.containd.app/';

/** Template Editor handbook (static docs; English). */
export const DOCUMENTATION_HANDBOOK_URL = 'https://documentation.containd.app/';

/** Explore published Spaces (Shares search / home). */
export const SHARES_PUBLIC_URL = `https://${SHARES_PUBLIC_HOST}/`;

/** Bump when shipping mutable lists chrome/card assets so clients bypass stale CDN cache. */
export const LISTS_ASSETS_CACHE_BUST = '20260904d';

/**
 * Public websites (Spaces + Catalog) GA4 stream.
 * Not the Containd app web stream (`G-2YD19CGPMT` on containd.web.app).
 * Keep in sync with packages/shared/src/seo/ga4Scripts.ts.
 */
export const CONTAIND_PUBLIC_GA4_MEASUREMENT_ID = 'G-VCQSSW9CXG';

/** Consent-gated gtag loader tags. Do not inline Google’s always-on snippet. */
export function renderPublicGa4ScriptTags(): string {
  return `<script>window.__CONTAIND_GA4_ID__='${CONTAIND_PUBLIC_GA4_MEASUREMENT_ID}';</script>
<script src="/_assets/site-consent.js" defer></script>
<script src="/_assets/site-analytics.js" defer></script>`;
}

/**
 * Firebase App Check reCAPTCHA v3 site key for consumer web (lists, catalog, share).
 * Override with `APP_CHECK_RECAPTCHA_SITE_KEY` at sync/deploy time.
 * Empty = App Check client init is skipped until configured.
 */
export const CONTAIND_APP_CHECK_RECAPTCHA_SITE_KEY =
  readNodeEnv('APP_CHECK_RECAPTCHA_SITE_KEY')?.trim() ||
  readNodeEnv('CONTAIND_APP_CHECK_RECAPTCHA_SITE_KEY')?.trim() ||
  '';

/** Optional debug token for local Hosting / emulator App Check (Firebase Console). */
export const CONTAIND_APP_CHECK_DEBUG_TOKEN =
  readNodeEnv('APP_CHECK_DEBUG_TOKEN')?.trim() || '';

/**
 * Web App Check is OFF until Console reCAPTCHA is ready.
 * Set APP_CHECK_WEB_ENABLED=true to inject script tags again.
 */
export function isAppCheckWebEnabled(): boolean {
  const v = readNodeEnv('APP_CHECK_WEB_ENABLED')?.trim();
  return v === 'true' || v === '1';
}

/**
 * Script tags: set site key globals + load app-check.js (after auth-session.js).
 * Returns empty string while web App Check is disabled.
 */
export function renderAppCheckScriptTags(cacheBust: string = LISTS_ASSETS_CACHE_BUST): string {
  if (!isAppCheckWebEnabled()) {
    return '';
  }
  const key = JSON.stringify(CONTAIND_APP_CHECK_RECAPTCHA_SITE_KEY);
  const debug = JSON.stringify(CONTAIND_APP_CHECK_DEBUG_TOKEN);
  return `<script>window.__CONTAIND_APP_CHECK_WEB_ENABLED__=true;window.__CONTAIND_APP_CHECK_SITE_KEY__=${key};window.__CONTAIND_APP_CHECK_DEBUG_TOKEN__=${debug};</script>
<script src="/_assets/app-check.js?v=${cacheBust}"></script>`;
}

/** Safe Node env read — never touches bare `process` (breaks Vite browser bundles). */
function readNodeEnv(key: string): string | undefined {
  try {
    const g = globalThis as { process?: { env?: Record<string, string | undefined> } };
    const v = g.process?.env?.[key];
    return typeof v === 'string' ? v : undefined;
  } catch {
    return undefined;
  }
}

/** Widget `data-action` for `/legal/report/` — must match Functions siteverify. */
export const LISTS_TURNSTILE_ACTION = 'report';

/**
 * Cloudflare Turnstile site key for containd.space (public).
 * Override with `TURNSTILE_SITE_KEY` (dummy `1x00000000000000000000AA` for local HTML).
 */
export const LISTS_TURNSTILE_SITE_KEY =
  readNodeEnv('TURNSTILE_SITE_KEY')?.trim() || '0x4AAAAAAETqeiKgZYdP_BNc';

/** Optional Amazon Associates storefront / search for catalog partnership strip. */
export const CATALOG_AMAZON_ASSOCIATE = {
  /** Set at soft-launch ops time; empty disables the strip. */
  tag: readNodeEnv('CATALOG_AMAZON_TAG') || '',
  storeUrl: 'https://www.amazon.com',
  disclosure:
    'As an Amazon Associate, Containd may earn from qualifying purchases.',
} as const;

export function catalogAmazonSearchUrl(query: string): string | null {
  const tag = CATALOG_AMAZON_ASSOCIATE.tag.trim();
  if (!tag) return null;
  const q = query.trim();
  if (!q) return null;
  const u = new URL(`${CATALOG_AMAZON_ASSOCIATE.storeUrl}/s`);
  u.searchParams.set('k', q);
  u.searchParams.set('tag', tag);
  return u.toString();
}

/** Deep link to import a remote collection ZIP (example collections). */
export function catalogImportListDeepLink(httpsUrl: string): string {
  return `containd://ImportList?url=${encodeURIComponent(httpsUrl)}`;
}

/** Deep link to import a category package JSON (Category Management). */
export function catalogImportCategoryDeepLink(httpsUrl: string): string {
  return `containd://ImportCategory?url=${encodeURIComponent(httpsUrl)}`;
}

/**
 * Public SSR example list URL for a catalog entry's `exampleShareSlug`.
 * Durable identity: https://containd.space/s/{slug}
 */
export function catalogExampleShareUrl(slug: string): string {
  const trimmed = slug.trim().replace(/^\/+|\/+$/g, '');
  return `https://${SHARES_PUBLIC_HOST}/s/${encodeURIComponent(trimmed)}`;
}

/** True when a manifest/card has a usable example share slug. */
export function hasExampleShareSlug(slug: string | null | undefined): boolean {
  return typeof slug === 'string' && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim());
}

/**
 * Whether catalog HTML should emit robots noindex.
 * Env `CATALOG_ROBOTS_NOINDEX=1` forces on; `=0` forces off; else soft-launch default.
 */
export function catalogRobotsNoindex(): boolean {
  const env = readNodeEnv('CATALOG_ROBOTS_NOINDEX');
  if (env === '1' || env === 'true') return true;
  if (env === '0' || env === 'false') return false;
  return CATALOG_SOFT_LAUNCH_NOINDEX;
}

export const CATALOG_COLLECTIONS = {
  SUBMISSIONS: 'catalogSubmissions',
  ENTRIES: 'catalogEntries',
  CLASS_INDEX: 'catalogClassIndex',
  INSTALL_EVENTS: 'catalogInstallEvents',
  /** Reverse index: catalogTemplateLists/{templateId}/lists/{shareId} */
  TEMPLATE_LISTS: 'catalogTemplateLists',
} as const;
